import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, ReactNode, useMemo, useState } from 'react';
import InputHeader from '../../../components/InputHeader';
import { useAppDataContext } from '../../../contexts/appData';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { Contract } from '../../../types/contract';
import { Operator } from '../../../types/operator';
import { PortSelectItem } from '../../../types/port';
import { Role } from '../../../types/user';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
  getNullableDayjsDifferenceChecker,
} from '../../../utils/date';
import { renderPortSelectItemLabel } from '../../../utils/port';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import { SelectItem, ServiceType } from '../../../types/core';

type ContractFormValues = {
  name: string;
  operatorId: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  serviceType: ServiceType | '';
  allowedPortsIds: string[];
  defaultDeparturePortId: string;
  defaultArrivalPortId: string;
  county: string;
};

export interface ContractFormSubmittedValues {
  name: string;
  operatorId: string;
  startDate: string;
  endDate: string;
  serviceType: ServiceType;
  allowedPortsIds: string[];
  defaultDeparturePortId: string;
  defaultArrivalPortId: string;
  county: string;
}

export interface ContractBasicInfoFormProps {
  contract: Contract | null;
  disabled?: boolean;
  isLoading?: boolean;
  onContractNameChange: (newName: string) => void;
  onSubmit: (values: ContractFormSubmittedValues, callback: () => void) => void;
  onCancel?: () => void;
}

const ContractBasicInfoForm = ({
  contract,
  onSubmit,
  onCancel,
  onContractNameChange,
  disabled = false,
  isLoading = false,
}: ContractBasicInfoFormProps) => {
  const { t } = useLanguage();
  const { hasRole } = useCurrentUser();

  const {
    ports: allPorts,
    portsMap,
    appData: { counties },
  } = useAppDataContext();
  const { getOperators } = useIotsApi();
  const [pendingEndDateConfirmation, setPendingEndDateConfirmation] = useState<{
    values: ContractFormValues;
    callback: () => void;
  } | null>(null);

  const {
    data: operators = [],
    isFetching: isFetchingOperators,
    error: operatorsError,
  } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: operators =>
      // Simple default sorting by name
      [...operators].sort((a, b) => a.name.localeCompare(b.name)),
  });

  useQueryErrorNotifier([operatorsError]);

  const selectablePorts = useMemo(
    () => allPorts.filter(({ issue }) => !issue),
    [allPorts],
  );

  const { initialValues, contractEndDate } = useMemo<{
    initialValues: ContractFormValues;
    contractEndDate: Dayjs | null;
  }>(() => {
    if (!contract) {
      return {
        initialValues: {
          name: '',
          operatorId: '',
          startDate: null,
          endDate: null,
          serviceType: '',
          allowedPortsIds: [],
          defaultDeparturePortId: '',
          defaultArrivalPortId: '',
          county: '',
        },
        contractEndDate: null,
      };
    }

    const contractEndDate = dayjs(contract.dateTo);

    return {
      initialValues: {
        name: contract.name,
        operatorId: contract.operatorId,
        startDate: dayjs(contract.dateFrom),
        endDate: contractEndDate,
        serviceType: contract.serviceType,
        allowedPortsIds: contract.allowedPorts,
        defaultDeparturePortId: contract.defaultPortOfDepartureId,
        defaultArrivalPortId: contract.defaultPortOfArrivalId,
        county: contract.county,
      },
      contractEndDate,
    };
  }, [contract]);

  const { countyItems, countyItemsMap } = useMemo(() => {
    const countyItems: SelectItem[] = [];
    const countyItemsMap: Record<string, SelectItem> = {};
    counties.forEach(county => {
      const item: SelectItem = {
        value: county,
        label: t(`counties.${county}`),
      };
      countyItems.push(item);
      countyItemsMap[county] = item;
    });
    countyItems.sort((a, b) => a.label.localeCompare(b.label));
    return { countyItems, countyItemsMap };
  }, [counties, t]);

  // This code considers that a user that is not an OPERATOR will always be a DRCD or ADMIN
  const isOperatorRole = hasRole(Role.OPERATOR);
  const areInputsDisabled = disabled || isLoading || isOperatorRole;

  const {
    formState,
    materialInputHelpers: {
      getBaseProps,
      getTextFieldProps,
      getSelectProps,
      renderError,
    },
  } = useIotsAdminForm(
    {
      isEditingByDefault: !contract,
      initialValues,
      onEditionCancel: onCancel,
      onSubmit: (values, callback) => {
        if (
          contractEndDate &&
          contractEndDate.isBefore(values.endDate as Dayjs)
        ) {
          // The values to be submitted are stored so it is ensured they don't change prior the user confirmation.
          setPendingEndDateConfirmation({ values, callback });
          return;
        }
        onSubmit(
          {
            name: values.name.trim(),
            operatorId: values.operatorId,
            startDate: (values.startDate as Dayjs).format(
              DAYJS_DATA_DATE_FORMAT,
            ),
            endDate: (values.endDate as Dayjs).format(DAYJS_DATA_DATE_FORMAT),
            serviceType: values.serviceType as ServiceType,
            allowedPortsIds: values.allowedPortsIds,
            defaultDeparturePortId: values.defaultDeparturePortId,
            defaultArrivalPortId: values.defaultArrivalPortId,
            county: values.county,
          },
          callback,
        );
      },
      validate: (values, setError) => {
        if (!values.name.trim()) {
          setError('name', t('validation.required-field'));
        }

        if (!values.operatorId) {
          setError('operatorId', t('validation.required-field'));
        }

        if (!values.startDate) {
          setError('startDate', t('validation.required-field'));
        } else if (!values.startDate.isValid()) {
          setError('startDate', t('validation.invalid-date'));
        }

        if (!values.endDate) {
          setError('endDate', t('validation.required-field'));
        } else if (!values.endDate.isValid()) {
          setError('endDate', t('validation.invalid-date'));
        } else if (
          values.startDate &&
          !values.endDate.isAfter(values.startDate, 'day')
        ) {
          setError('endDate', t('validation.invalid-end-date'));
        }

        if (!values.serviceType) {
          setError('serviceType', t('validation.required-field'));
        }

        if (!values.county) {
          setError('county', t('validation.required-field'));
        }

        if (values.allowedPortsIds.length < 2) {
          setError(
            'allowedPortsIds',
            t('validation.minimum-selection', {
              MINIMUM_SELECTED_OPTIONS: '2',
            }),
          );
        } else if (
          values.allowedPortsIds.some(portId =>
            Boolean(portsMap[portId]?.issue),
          )
        ) {
          setError('allowedPortsIds', t('validation.invalid-options-selected'));
        }

        if (!values.defaultDeparturePortId) {
          setError('defaultDeparturePortId', t('validation.required-field'));
        } else if (portsMap[values.defaultDeparturePortId]?.issue) {
          setError('defaultDeparturePortId', t('validation.invalid-option'));
        }

        if (!values.defaultArrivalPortId) {
          setError('defaultArrivalPortId', t('validation.required-field'));
        } else if (
          values.defaultArrivalPortId === values.defaultDeparturePortId
        ) {
          setError(
            'defaultArrivalPortId',
            t('validation.value-equal-to-another-field', {
              ANOTHER_FIELD: t('content.default-departure-port'),
            }),
          );
        } else if (portsMap[values.defaultArrivalPortId]?.issue) {
          setError('defaultArrivalPortId', t('validation.invalid-option'));
        }
      },
      differenceChecker: {
        allowedPortsIds: (a: string[], b: string[]) =>
          a.length !== b.length ||
          (a.length > 0 && a.some(x => !b.includes(x))),
        startDate: getNullableDayjsDifferenceChecker('day'),
        endDate: getNullableDayjsDifferenceChecker('day'),
      },
      resetOnInitialValuesChange: () => !!contract,
    },
    { shouldInputsBeDisabled: areInputsDisabled },
  );

  const handleDialogConfirm = () => {
    // Resubmit the form now that the user confirmed if it is still valid
    if (pendingEndDateConfirmation) {
      // It is ensured that the values pending confirmation are already validated, so no more check needed
      const {
        values: {
          allowedPortsIds,
          county,
          defaultArrivalPortId,
          defaultDeparturePortId,
          endDate,
          name,
          operatorId,
          serviceType,
          startDate,
        },
        callback,
      } = pendingEndDateConfirmation;

      onSubmit(
        {
          name: name.trim(),
          operatorId,
          startDate: (startDate as Dayjs).format(DAYJS_DATA_DATE_FORMAT),
          endDate: (endDate as Dayjs).format(DAYJS_DATA_DATE_FORMAT),
          serviceType: serviceType as ServiceType,
          allowedPortsIds,
          defaultDeparturePortId,
          defaultArrivalPortId,
          county,
        },
        callback,
      );
      setPendingEndDateConfirmation(null);
    }
  };

  const handleDialogClose = () => {
    setPendingEndDateConfirmation(null);
  };

  return (
    <CommonMainPanelForm
      isEditing={formState.isEditing}
      onSubmit={formState.handleSubmit}
      onCancelEdition={formState.cancelEdition}
      onEditionRequest={formState.requestEdition}
      isSubmitting={isLoading}
      isSubmitDisabled={
        areInputsDisabled ||
        !formState.haveValuesChanged ||
        formState.hasVisibleErrors
      }
      isEditDisabled={areInputsDisabled}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <label className="block mb-4">
          <InputHeader required>{t('content.contract-name')}</InputHeader>
          <TextField
            {...getTextFieldProps('name')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const newName = event.target.value;
              formState.updateValues({ name: newName });
              onContractNameChange(newName);
            }}
            placeholder={t('content.contract-name')}
          />
        </label>
        <label className="block mb-4">
          <InputHeader required>{t('content.operator')}</InputHeader>
          <FormControl error={getBaseProps('operatorId').error}>
            <Autocomplete<Operator>
              value={
                operators.find(
                  ({ id }) => id === formState.values.operatorId,
                ) || null
              }
              onChange={(_event, selectedOption) =>
                formState.updateValues(
                  { operatorId: selectedOption?.id ?? '' },
                  true,
                )
              }
              options={operators}
              onBlur={getBaseProps('operatorId').onBlur}
              loading={isFetchingOperators}
              disabled={
                getBaseProps('operatorId').disabled || isFetchingOperators
              }
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('operatorId').error}
                  placeholder={t('content.select')}
                />
              )}
              getOptionLabel={operator =>
                `${operator.name}${operator.isActive ? '' : ` (${t('content.inactive')})`}`
              }
              getOptionDisabled={option => !option.isActive}
            />
            {renderError('operatorId')}
          </FormControl>
        </label>
        <div className="flex gap-4 items-start justify-between">
          <label className="block mb-4 w-full">
            <InputHeader required>{t('content.start-date')}</InputHeader>
            <FormControl error={getBaseProps('startDate').error}>
              <DatePicker
                className="w-full"
                disabled={getBaseProps('startDate').disabled || !!contract}
                format={DAYJS_VISUALIZATION_DATE_FORMAT}
                slotProps={{
                  textField: {
                    className: 'py-1.5',
                    error: getBaseProps('startDate').error,
                    onBlur: getBaseProps('startDate').onBlur,
                  },
                }}
                value={
                  formState.values.startDate
                    ? dayjs(formState.values.startDate)
                    : null
                }
                onChange={newValue =>
                  formState.updateValues({
                    startDate: newValue,
                  })
                }
              />
              {renderError('startDate')}
            </FormControl>
          </label>

          <label className="block mb-4 w-full">
            <InputHeader required>{t('content.end-date')}</InputHeader>
            <FormControl error={getBaseProps('endDate').error}>
              <DatePicker
                className="w-full"
                disabled={getBaseProps('endDate').disabled || !!contract}
                format={DAYJS_VISUALIZATION_DATE_FORMAT}
                minDate={dayjs(formState.values.startDate)}
                slotProps={{
                  textField: {
                    className: 'py-1.5',
                    error: getBaseProps('endDate').error,
                    onBlur: getBaseProps('endDate').onBlur,
                  },
                }}
                value={
                  formState.values.endDate
                    ? dayjs(formState.values.endDate)
                    : null
                }
                onChange={newValue =>
                  formState.updateValues({
                    endDate: newValue,
                  })
                }
              />
              {renderError('endDate')}
            </FormControl>
          </label>
          <ConfirmationDialog
            open={Boolean(pendingEndDateConfirmation)}
            onClose={handleDialogClose}
            onConfirm={handleDialogConfirm}
            title={t('content.contract-end-date-extension')}
          >
            <p>{t('content.contract-end-date-extension-warning')}</p>
          </ConfirmationDialog>
        </div>
        <label className="block mb-4">
          <InputHeader required>{t('content.service-type')}</InputHeader>
          <FormControl error={getBaseProps('serviceType').error}>
            <Select
              {...getSelectProps('serviceType')}
              displayEmpty
              renderValue={selected => {
                if (!selected) {
                  return (
                    <span className="text-iots-grey-400">
                      {t('content.select')}
                    </span>
                  );
                }
                return t(`service-types.${selected}.name`);
              }}
            >
              {Object.values(ServiceType)
                .filter(
                  serviceType =>
                    ![
                      ServiceType.AirService,
                      ServiceType.FacilityManagement,
                    ].includes(serviceType),
                )
                .map(serviceType => (
                  <MenuItem key={serviceType} value={serviceType}>
                    {t(`service-types.${serviceType}.name`)}
                  </MenuItem>
                ))}
            </Select>
            {renderError('serviceType')}
          </FormControl>
        </label>
        <label className="block mb-4">
          <InputHeader
            required
            helperText={t('content.allowed-ports-tooltip')}
            warningText={
              contract && formState.isEditing
                ? t('content.allowed-ports-edition-warning')
                : undefined
            }
          >
            {t('content.allowed-ports')}
          </InputHeader>
          <FormControl error={getBaseProps('allowedPortsIds').error}>
            <Autocomplete<PortSelectItem, true>
              multiple
              disableCloseOnSelect
              value={formState.values.allowedPortsIds.reduce((acc, id) => {
                const port = portsMap[id];
                if (port) {
                  acc.push(port);
                }
                return acc;
              }, [] as PortSelectItem[])}
              onChange={(
                _event: React.SyntheticEvent,
                selectedOptions: PortSelectItem[],
              ) => {
                formState.updateValues(
                  {
                    allowedPortsIds: selectedOptions.map(opt => opt.value),
                  },
                  true,
                );
                if (formState.values.defaultDeparturePortId) {
                  formState.updateValues(
                    {
                      defaultDeparturePortId: '',
                    },
                    false,
                  );
                }
                if (formState.values.defaultArrivalPortId) {
                  formState.updateValues(
                    {
                      defaultArrivalPortId: '',
                    },
                    false,
                  );
                }
              }}
              options={selectablePorts}
              onBlur={getBaseProps('allowedPortsIds').onBlur}
              disabled={getBaseProps('allowedPortsIds').disabled}
              renderInput={params => (
                <TextField {...params} placeholder={t('content.select')} />
              )}
              getOptionKey={option => option.value}
              getOptionLabel={port => renderPortSelectItemLabel(port, t)}
              getOptionDisabled={option => Boolean(option.issue)}
            />
            {renderError('allowedPortsIds')}
          </FormControl>
        </label>
        <div className="flex gap-4 items-start justify-between">
          <label className="block mb-4 w-full">
            <InputHeader required>
              {t('content.default-departure-port')}
            </InputHeader>
            <FormControl error={getBaseProps('defaultDeparturePortId').error}>
              <Select<string>
                {...getSelectProps('defaultDeparturePortId')}
                disabled={
                  getBaseProps('defaultDeparturePortId').disabled ||
                  formState.values.allowedPortsIds.length < 2
                }
                displayEmpty
                renderValue={id => {
                  if (!id) {
                    return (
                      <span className="text-iots-grey-400">
                        {t('content.select')}
                      </span>
                    );
                  }
                  const port = portsMap[id];
                  return port ? renderPortSelectItemLabel(port, t) : '-';
                }}
              >
                {formState.values.allowedPortsIds.reduce((acc, id) => {
                  const port = portsMap[id];
                  if (port) {
                    acc.push(
                      <MenuItem
                        key={port.value}
                        value={port.value}
                        disabled={Boolean(port.issue)}
                      >
                        {renderPortSelectItemLabel(port, t)}
                      </MenuItem>,
                    );
                  }
                  return acc;
                }, [] as ReactNode[])}
              </Select>
              {renderError('defaultDeparturePortId')}
            </FormControl>
          </label>
          <label className="block mb-4 w-full">
            <InputHeader required>
              {t('content.default-arrival-port')}
            </InputHeader>
            <FormControl error={getBaseProps('defaultArrivalPortId').error}>
              <Select<string>
                {...getSelectProps('defaultArrivalPortId')}
                disabled={
                  getBaseProps('defaultArrivalPortId').disabled ||
                  formState.values.allowedPortsIds.length < 2
                }
                displayEmpty
                renderValue={id => {
                  if (!id) {
                    return (
                      <span className="text-iots-grey-400">
                        {t('content.select')}
                      </span>
                    );
                  }
                  const port = portsMap[id];
                  return port ? renderPortSelectItemLabel(port, t) : '-';
                }}
              >
                {formState.values.allowedPortsIds.reduce((acc, id) => {
                  const port = portsMap[id];
                  if (
                    port &&
                    port.value !== formState.values.defaultDeparturePortId
                  ) {
                    acc.push(
                      <MenuItem
                        key={port.value}
                        value={port.value}
                        disabled={Boolean(port.issue)}
                      >
                        {renderPortSelectItemLabel(port, t)}
                      </MenuItem>,
                    );
                  }
                  return acc;
                }, [] as ReactNode[])}
              </Select>
              {renderError('defaultArrivalPortId')}
            </FormControl>
          </label>
        </div>
        <label className="block mb-4">
          <InputHeader required>{t('content.county')}</InputHeader>
          <FormControl error={getBaseProps('county').error}>
            <Autocomplete<SelectItem>
              value={countyItemsMap[formState.values.county] ?? null}
              onChange={(_event, selectedOption) =>
                formState.updateValues(
                  { county: selectedOption?.value ?? '' },
                  true,
                )
              }
              getOptionLabel={value => value.label}
              options={countyItems}
              onBlur={getBaseProps('county').onBlur}
              disabled={getBaseProps('county').disabled}
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('county').error}
                  placeholder={t('content.select')}
                />
              )}
            />
            {renderError('county')}
          </FormControl>
        </label>
      </LocalizationProvider>
    </CommonMainPanelForm>
  );
};

export default ContractBasicInfoForm;
